import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {computeMultiSelectCheckboxState} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {useAsyncTableSource} from '../../../../../../../../src/components/common/table/helpers/useAsyncTableSource';
import {TableColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableMultiSelectHeaderRow} from '../../../../../../../../src/components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {VirtualTable} from '../../../../../../../../src/components/common/table/virtualTable';
import {
  VirtualTableRowRenderer,
  VirtualTableRowSiblingData,
} from '../../../../../../../../src/components/common/table/virtualTableBase';
import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {TableOffsetPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {
  Handler,
  HandlerOf,
  HandlerOfWithOptional,
  Renderer,
} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {
  getAllTableColumnsInOrder,
  renderContactsTableMultiSelectCardCount,
} from '../common/contacts/contactsTableHelpers';
import {ContactsTableRowContainer} from '../common/contacts/contactsTableRowContainer';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {SharedColumnsEnum} from '../common/sharedTableConfig';
import {computeBulkSelectedValues} from '../common/tableHelpers';

/*
 * Props.
 */

interface ContactsSearchTableProps {
  cardIds: ReadonlyArray<number>;
  multiSelectHeaderActions: ReactNode;
  isInMultiSelectMode: boolean;
  selectedCardIds: ReadonlyArray<number>;
  dynamicColumns: TableColumn<SharedColumnsEnum, ContactsManagerCustomFieldColumnMetadata | undefined>[];
  paginationData?: TableOffsetPaginationDataModel;
  query: string;

  renderEmptyState: Renderer;
  addSelectedCardIds: HandlerOfWithOptional<ReadonlyArray<number>, boolean>;
  removeSelectedCardIds: HandlerOf<ReadonlyArray<number>>;
  onReset: Handler;
  onFetchAsync: (query: string, cancelToken: CancelToken) => Promise<void>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 0px;
  position: relative;
`;

/*
 * Component.
 */

export const ContactsSearchTable: FC<ContactsSearchTableProps> = ({
  cardIds,
  multiSelectHeaderActions,
  isInMultiSelectMode,
  selectedCardIds,
  dynamicColumns,
  paginationData,
  query,
  renderEmptyState,
  addSelectedCardIds,
  removeSelectedCardIds,
  onReset,
  onFetchAsync,
}) => {
  const allTableColumns = getAllTableColumnsInOrder(dynamicColumns);
  const [lastSelectedCardId, setLastSelectedCardId] = useState<number>();
  const {panelId} = useContactsManagerContext();

  const renderRow: VirtualTableRowRenderer<number> = (
    cardId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <ContactsTableRowContainer
      isSelected={selectedCardIds.includes(cardId)}
      isPreviousSiblingSelected={selectedCardIds.includes(siblingData?.previous ?? -1)}
      isNextSiblingSelected={selectedCardIds.includes(siblingData?.next ?? -1)}
      cardId={cardId}
      panelId={panelId}
      options={options}
      tableColumns={allTableColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      onSelect={() => {
        addSelectedCardIds([cardId]);
        setLastSelectedCardId(cardId);
      }}
      onMultiSelect={() => {
        addSelectedCardIds(computeBulkSelectedValues(cardId, lastSelectedCardId, cardIds), true);
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          addSelectedCardIds([cardId], true);
          setLastSelectedCardId(cardId);
        } else {
          removeSelectedCardIds([cardId]);
        }
      }}
    />
  );

  const renderMultiSelectHeader = (onMultiSelectHeaderChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      onMultiSelectChange={onMultiSelectHeaderChange}
      multiSelectStatus={computeMultiSelectCheckboxState(cardIds.length, selectedCardIds.length)}
      renderTotalItemsLabel={() => renderContactsTableMultiSelectCardCount(selectedCardIds.length)}
    >
      {multiSelectHeaderActions}
    </TableMultiSelectHeaderRow>
  );

  const source = useAsyncTableSource(
    {
      fetcher: ({cancelToken}) => onFetchAsync(query, cancelToken),
      onReset: async () => onReset(),
    },
    [query],
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        columns={allTableColumns}
        source={source}
        paginationData={paginationData}
        items={cardIds}
        size={VisualSizesEnum.SMALL}
        renderRow={renderRow}
        renderEmpty={renderEmptyState}
        shouldAllowMultiSelect
        isInMultiSelectMode={isInMultiSelectMode}
        renderMultiSelectHeader={renderMultiSelectHeader}
        onMultiSelectHeaderChange={(checked) => {
          if (checked) {
            addSelectedCardIds(cardIds);
          } else {
            removeSelectedCardIds(cardIds);
          }
        }}
      />
    </StyledTableContainerDiv>
  );
};
